/**
 * The financial summary as a person edits it (DOP-637): the values they
 * type, the rows and years they add, and the note on where each value came
 * from.
 *
 * The table is built from the run's facts; a draft sits on top of it and is
 * sent with "Save as a new version". The server records each value as an
 * entry, exactly as the Needs you tab does, so a typed value reads "entered by
 * <name>" with its note wherever the memo shows it. Computed rows (margins,
 * growth) are never typed in: they follow from their inputs when the version
 * is saved.
 *
 *  - a row the settings list but the materials never gave is added back
 *    (`rows`), and filled like any other;
 *  - a row the settings do not list is the deal's own (`custom`), keyed
 *    `custom_<name>` the way the server keys it;
 *  - a column is added blank at the right end and named by the person, any
 *    name ("FY2027P", "Pro forma"; DOP-649). A value is typed under it once
 *    it has a name; a column left empty or unnamed is dropped on save;
 *  - the columns and rows can be dragged into another order (`columnOrder`,
 *    `rowOrder`), saved with the version and followed wherever it prints.
 *
 * The memo's cover rides in the same draft (DOP-641): a company fact or the
 * tagline typed over (`header`), saved as the same kind of entry.
 */

import { stripMarks } from "./inlineMarks";
import type {
	DealScreeningCustomFigure,
	DealScreeningFact,
	DealScreeningFactEntry,
	DealScreeningHeaderEntry,
	DealScreeningRunFigure,
} from "../../types/dealScreening";
import { type DocumentSource, sentSource, sourceText } from "./documentSource";
import { formatTableValue } from "./dealScreeningFormat";
import { isAbsentOptional } from "./FinancialSummaryTable";
import type { MemoChange } from "./memoEditorModel";
import { periodsOf } from "./memoModel";
import { inSavedOrder } from "./tableOrder";

export type FigureEntry = DealScreeningFactEntry;
export type FigureUnit = DealScreeningCustomFigure["unit"];

export interface FiguresDraft {
	entries: FigureEntry[];
	custom: DealScreeningCustomFigure[];
	/** Columns added, by key: a new column's key until it is named (DOP-649). */
	periods: string[];
	/** A new column's name as typed, by its key (DOP-649). */
	columnNames?: Record<string, string>;
	/** The columns as dragged, by key; null when not reordered (DOP-649). */
	columnOrder?: string[] | null;
	/** The rows as dragged, by key; null when not reordered (DOP-649). */
	rowOrder?: string[] | null;
	/** Whether the person dragged a column or row (DOP-649); adding a column is not a move. */
	moved?: boolean;
	/** Figures the settings list and the materials did not give, added back as rows. */
	rows: string[];
	/** Company facts and the tagline as typed (DOP-641). */
	header: HeaderEdit[];
	/** The company facts heading as typed; null when it is as it was (DOP-641). */
	coverTitle?: string | null;
	/** The deal's name as typed over the memo's title; null when it is as it was (DOP-641). */
	dealName?: string | null;
}

/** A company fact or the tagline as the person typed it, not yet parsed. */
export interface HeaderEdit {
	key: string;
	unit: string;
	text: string;
	note: string;
	/** The document the value came from (DOP-652). */
	source?: DocumentSource;
}

export const EMPTY_FIGURES: FiguresDraft = {
	entries: [],
	custom: [],
	periods: [],
	rows: [],
	header: [],
	coverTitle: null,
	dealName: null,
	columnNames: {},
	columnOrder: null,
	rowOrder: null,
};

/** The draft with the deal's name as typed; typed back, or emptied, it is as it was. */
export const withDealName = (draft: FiguresDraft, text: string, start: string): FiguresDraft => ({
	...draft,
	dealName: text.trim() === "" || text.trim() === start.trim() ? null : text,
});

/** The deal's new name, or null: a rename is the deal's, not the version's (DOP-641). */
export const dealRename = (draft: FiguresDraft): string | null =>
	// The name is plain (DOP-660): formatting typed into it is taken off.
	stripMarks(draft.dealName ?? "").replace(/\s+/g, " ").trim().slice(0, 200) || null;

/** The draft with the company facts heading as typed; typed back, or emptied, it is as it was. */
export const withCoverTitle = (draft: FiguresDraft, text: string, start: string): FiguresDraft => ({
	...draft,
	coverTitle: text.trim() === "" || text.trim() === start.trim() ? null : text,
});

// ------------------------------------------------------------------ periods

const yearOf = (period: string | null | undefined): number | null => {
	const match = /^FY(\d{4})/.exec(period ?? "");
	return match ? Number(match[1]) : null;
};

/** One fiscal year is one column: FY2025A and FY2025P are the same year. */
export const samePeriod = (a: string | null, b: string | null): boolean =>
	a === b || (yearOf(a) !== null && yearOf(a) === yearOf(b));

/** A new column's key while it is named (DOP-649): stable, so typing its name keeps the field. */
const NEW_COLUMN = "column_new_";
export const isNewColumn = (period: string | null): boolean => Boolean(period?.startsWith(NEW_COLUMN));

/** A column's name as it reads: a new column's as typed, any other's its own. */
export const columnName = (draft: FiguresDraft, period: string): string =>
	isNewColumn(period) ? (draft.columnNames?.[period] ?? "") : period;

/** The name a new column is saved under, or null while it has none. */
const savedColumnName = (draft: FiguresDraft, period: string): string | null =>
	isNewColumn(period) ? columnName(draft, period).replace(/\s+/g, " ").trim().slice(0, 32) || null : period;

// ------------------------------------------------------------------ the grid

export interface FigureCell {
	/** Null for a figure with one latest value. */
	period: string | null;
	fact: DealScreeningFact | null;
	/** What the person typed here, when they did. */
	entry: FigureEntry | null;
}

export interface FigureRow {
	key: string;
	label: string;
	unit: string;
	derived: boolean;
	custom: boolean;
	point: boolean;
	cells: FigureCell[];
}

export interface FiguresGrid {
	periods: string[];
	/** Rows with one value per year. */
	series: FigureRow[];
	/** Rows with one latest value (cash, debt). */
	point: FigureRow[];
	/** Figures the settings list that are not rows yet: what "Add a row" offers. */
	addable: DealScreeningRunFigure[];
}

/** The order the version was saved with (DOP-649): columns by name, rows by key. */
export interface SavedTableOrder {
	columns: string[];
	rows: string[];
}

export function figuresGrid(
	facts: DealScreeningFact[],
	figures: DealScreeningRunFigure[] | undefined,
	draft: FiguresDraft,
	saved: SavedTableOrder = { columns: [], rows: [] },
): FiguresGrid {
	const printed = facts.filter((fact) => fact.kind !== "header" && fact.printed !== false);
	const known = periodsOf(printed);
	// A column added goes where it was added, at the right end, not among
	// the years (DOP-649); then the order dragged, or the order saved.
	const periods = inSavedOrder(
		[...known, ...draft.periods.filter((p) => !known.some((k) => samePeriod(k, p)))],
		draft.columnOrder ?? saved.columns,
		(period) => period,
	);
	const meta = new Map((figures ?? []).map((figure) => [figure.key, figure]));
	const customs = new Map(draft.custom.map((figure) => [figure.key, figure]));

	const factsByKey = new Map<string, DealScreeningFact[]>();
	[...printed]
		.sort((a, b) => a.sort_order - b.sort_order)
		.forEach((fact) => factsByKey.set(fact.key, [...(factsByKey.get(fact.key) ?? []), fact]));
	const keys = [
		...Array.from(factsByKey.keys()),
		...draft.custom.map((figure) => figure.key).filter((key) => !factsByKey.has(key)),
	];

	const entryAt = (key: string, period: string | null) =>
		draft.entries.find((entry) => entry.key === key && samePeriod(entry.period, period)) ?? null;
	const series: FigureRow[] = [];
	const point: FigureRow[] = [];
	const shown = new Set<string>();

	keys.forEach((key) => {
		const rowFacts = factsByKey.get(key) ?? [];
		const added = customs.get(key);
		const hasEntry = draft.entries.some((entry) => entry.key === key);
		if (!added && isAbsentOptional(rowFacts) && !draft.rows.includes(key) && !hasEntry) return;
		const figure = meta.get(key);
		const first = rowFacts[0];
		const isPoint = figure
			? figure.periods === "point"
			: added
				? added.periods === "point"
				: rowFacts.every((fact) => fact.period === null) &&
					!periods.some((p) => rowFacts.some((fact) => fact.period === p));
		const row: FigureRow = {
			key,
			label: first?.label ?? added?.label ?? figure?.label ?? key,
			unit: first?.unit ?? added?.unit ?? figure?.unit ?? "usd_m",
			derived: first ? first.kind === "derived" : figure?.kind === "derived",
			custom: Boolean(added) || Boolean(figure?.custom),
			point: isPoint,
			cells: isPoint
				? [{ period: null, fact: first ?? null, entry: entryAt(key, null) }]
				: periods.map((period) => ({
						period,
						fact: rowFacts.find((fact) => samePeriod(fact.period, period)) ?? null,
						entry: entryAt(key, period),
					})),
		};
		shown.add(key);
		(isPoint ? point : series).push(row);
	});

	const addable = (figures ?? []).filter(
		(figure) => figure.kind === "input" && !shown.has(figure.key),
	);
	const rowOrder = draft.rowOrder ?? saved.rows;
	return {
		periods,
		series: inSavedOrder(series, rowOrder, (row) => row.key),
		point: inSavedOrder(point, rowOrder, (row) => row.key),
		addable,
	};
}

// ------------------------------------------------------------------ changes to the draft

const sameSlot = (entry: FigureEntry, key: string, period: string | null) =>
	entry.key === key && samePeriod(entry.period, period);

export const withEntry = (draft: FiguresDraft, entry: FigureEntry): FiguresDraft => ({
	...draft,
	entries: [...draft.entries.filter((e) => !sameSlot(e, entry.key, entry.period)), entry],
});

export const withoutEntry = (draft: FiguresDraft, key: string, period: string | null): FiguresDraft => ({
	...draft,
	entries: draft.entries.filter((e) => !sameSlot(e, key, period)),
});

/** The key the server makes from a name: "custom_" and the name in snake case. */
export const customKey = (label: string): string => {
	const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
	return `custom_${(slug || "section").slice(0, 100)}`;
};

export const withRow = (draft: FiguresDraft, key: string): FiguresDraft =>
	draft.rows.includes(key) ? draft : { ...draft, rows: [...draft.rows, key] };

// ------------------------------------------------------------------ typed in place (DOP-641)

/** A new row's key while it is typed: stable, so the row keeps its field; made from its name on save. */
const NEW_ROW = "custom_new_";
export const isNewRow = (key: string): boolean => key.startsWith(NEW_ROW);

/** The draft with a blank row at the bottom, and its key, for its name field to take the caret. */
export function withNewRow(draft: FiguresDraft): { draft: FiguresDraft; key: string } {
	const used = draft.custom.filter((f) => isNewRow(f.key)).map((f) => Number(f.key.slice(NEW_ROW.length)) || 0);
	const key = `${NEW_ROW}${Math.max(0, ...used) + 1}`;
	return { draft: { ...draft, custom: [...draft.custom, { key, label: "", unit: "usd_m", periods: "by_year" }] }, key };
}

/** A row's name as typed. */
export const withRowLabel = (draft: FiguresDraft, key: string, label: string): FiguresDraft => ({
	...draft,
	custom: draft.custom.map((f) => (f.key === key ? { ...f, label: label.slice(0, 120) } : f)),
});

/**
 * A new row named like a figure the settings list is that figure (DOP-641):
 * the row, and what was typed in it, move to it. Anything else stays as it is.
 */
export function withListedRow(draft: FiguresDraft, grid: FiguresGrid, key: string): FiguresDraft {
	const figure = draft.custom.find((f) => f.key === key);
	const listed = figure && grid.addable.find((f) => f.label.toLowerCase() === figure.label.trim().toLowerCase());
	if (!figure || !listed) return draft;
	return {
		...withRow(draft, listed.key),
		custom: draft.custom.filter((f) => f.key !== key),
		entries: draft.entries.map((e) => (e.key === key ? { ...e, key: listed.key } : e)),
	};
}

/**
 * What a person typed in a cell (DOP-641), or why it is not a value: empty
 * takes their value out; a number goes in, keeping the cell's note. A row
 * they added takes its unit from how they write it ("12%", "2.1x").
 */
export function withCellText(
	draft: FiguresDraft,
	row: FigureRow,
	period: string | null,
	text: string,
	found: number | null,
): FiguresDraft | null {
	const clean = text.trim();
	const had = draft.entries.find((e) => sameSlot(e, row.key, period));
	const note = had?.note ?? "";
	if (!clean) return withoutEntry(draft, row.key, period);
	let unit = row.unit;
	let next = draft;
	const added = draft.custom.find((f) => f.key === row.key);
	if (added) {
		const said = /%$/.test(clean) ? "pct" : /\dx$/i.test(clean) ? "x" : null;
		if (said && said !== added.unit) {
			unit = said;
			next = { ...draft, custom: draft.custom.map((f) => (f.key === row.key ? { ...f, unit: said } : f)) };
		}
	}
	const value = parseFigure(clean, unit);
	if (value === null) return null;
	// A document vouches for the value it was given for (DOP-652): typed
	// over, the value is the person's own and the document is taken off.
	const source = had?.source && Math.abs(had.value - value) < 1e-9 ? had.source : undefined;
	// Typed back to what the files say, with no source: it is theirs again.
	if (found !== null && Math.abs(found - value) < 1e-9 && !note && !source) {
		return withoutEntry(next, row.key, period);
	}
	return withEntry(next, { key: row.key, period, value, note, ...(source ? { source } : {}) });
}

/** A cell's note on where its value came from; a found value noted becomes the person's. */
export function withCellNote(
	draft: FiguresDraft,
	row: FigureRow,
	period: string | null,
	note: string,
	found: number | null,
): FiguresDraft {
	const entry = draft.entries.find((e) => sameSlot(e, row.key, period));
	const value = entry?.value ?? found;
	if (value === null || value === undefined) return draft;
	if (!note.trim() && !entry) return draft;
	return withEntry(draft, {
		key: row.key,
		period,
		value,
		note: note.trim(),
		...(entry?.source ? { source: entry.source } : {}),
	});
}

/** A cell's document taken off (DOP-652); what is typed and its note stay. */
export function withoutCellSource(draft: FiguresDraft, key: string, period: string | null): FiguresDraft {
	return {
		...draft,
		entries: draft.entries.map((entry) => {
			if (!sameSlot(entry, key, period)) return entry;
			const { source: _taken, ...rest } = entry;
			return rest;
		}),
	};
}

/** A row the person added, taken out again with whatever they typed in it. */
export const withoutRow = (draft: FiguresDraft, key: string): FiguresDraft => ({
	...draft,
	custom: draft.custom.filter((figure) => figure.key !== key),
	rows: draft.rows.filter((row) => row !== key),
	entries: draft.entries.filter((entry) => entry.key !== key),
	rowOrder: draft.rowOrder?.filter((row) => row !== key) ?? null,
});

/**
 * The draft with a blank column at the right end, and its key, for its name
 * field to take the caret (DOP-649). `columns` is the table's columns as
 * shown: the order is kept from here, so a column saved lands where it was
 * added, not sorted among the others by name (codex r3).
 */
export function withNewColumn(draft: FiguresDraft, columns: string[] = []): { draft: FiguresDraft; key: string } {
	const used = draft.periods.filter(isNewColumn).map((p) => Number(p.slice(NEW_COLUMN.length)) || 0);
	const key = `${NEW_COLUMN}${Math.max(0, ...used) + 1}`;
	return {
		draft: {
			...draft,
			periods: [...draft.periods, key],
			columnNames: { ...draft.columnNames, [key]: "" },
			columnOrder: [...(draft.columnOrder ?? columns), key],
		},
		key,
	};
}

/** A new column's name as typed. */
export const withColumnName = (draft: FiguresDraft, key: string, name: string): FiguresDraft => ({
	...draft,
	columnNames: { ...draft.columnNames, [key]: name.slice(0, 32) },
});

/** Why a new column's name will not do, or null: another column already has it. */
export function columnNameProblem(draft: FiguresDraft, grid: FiguresGrid, key: string): string | null {
	const name = savedColumnName(draft, key);
	if (!name) return null;
	const taken = grid.periods.some((p) => p !== key && samePeriod(savedColumnName(draft, p), name));
	return taken ? `${name} is already a column.` : null;
}

/** The first column name that clashes with another, or null: a save waits until it is fixed (DOP-649). */
export const columnsProblem = (draft: FiguresDraft, grid: FiguresGrid): string | null =>
	draft.periods.map((period) => columnNameProblem(draft, grid, period)).find(Boolean) ?? null;

/** A column the person added, taken out again with whatever they typed under it. */
export const withoutPeriod = (draft: FiguresDraft, period: string): FiguresDraft => {
	const names = Object.fromEntries(Object.entries(draft.columnNames ?? {}).filter(([key]) => key !== period));
	return {
		...draft,
		periods: draft.periods.filter((p) => p !== period),
		columnNames: names,
		columnOrder: draft.columnOrder?.filter((p) => p !== period) ?? null,
		entries: draft.entries.filter((entry) => !samePeriod(entry.period, period)),
	};
};

/** The columns in the order dragged (DOP-649). */
export const withColumnOrder = (draft: FiguresDraft, periods: string[]): FiguresDraft => ({
	...draft,
	columnOrder: periods,
	moved: true,
});

/** The rows in the order dragged (DOP-649): every row of both tables, by key. */
export const withRowOrder = (draft: FiguresDraft, keys: string[]): FiguresDraft => ({
	...draft,
	rowOrder: keys,
	moved: true,
});

// ------------------------------------------------------------------ values as typed

/**
 * A typed value as the figure's unit stores it, or null when it is not one.
 * Values are typed the way the table prints them: money in $mm ("4.5" or
 * "$4.5mm"), a percentage as shown ("12%" or "12", kept as a fraction), a
 * multiple with or without its "x", and "(1.2)" or "-1.2" is negative.
 * Money written out in dollars ("$100,000,000") is read as dollars, and
 * "250k" as thousands (DOP-660; `plainMoney` has the rule).
 */
export function parseFigure(text: string, unit: string): number | null {
	const grouped = text.includes(",");
	let clean = text.trim().replace(/[,\s]/g, "");
	if (!clean) return null;
	let sign = 1;
	if (/^\(.*\)$/.test(clean)) {
		sign = -1;
		clean = clean.slice(1, -1);
	}
	if (clean.startsWith("-")) {
		sign *= -1;
		clean = clean.slice(1);
	}
	clean = clean.replace(/^\$/, "");
	if (unit === "pct") clean = clean.replace(/%$/, "");
	else if (unit === "x") clean = clean.replace(/x$/i, "");
	else if (unit === "per_share") clean = clean.replace(/\/sh$/i, "");
	// Money says its own scale: "25mm" is millions, "250k" thousands.
	const scale = unit === "usd_m" ? /(mm|m|k)$/i.exec(clean)?.[1].toLowerCase() : undefined;
	if (scale) clean = clean.slice(0, -scale.length);
	if (!/^\d*\.?\d+$|^\d+\.$/.test(clean)) return null;
	if (!Number.isFinite(Number(clean))) return null;
	if (unit === "usd_m") {
		return sign * (scale === "k" ? Number(clean) / 1000 : scale ? Number(clean) : plainMoney(clean, grouped));
	}
	const number = sign * Number(clean);
	return unit === "pct" ? number / 100 : number;
}

/**
 * Money typed with no "mm" or "k", in $mm (DOP-660). `digits` is the number
 * with its sign, "$" and commas taken off; `grouped` is whether it had commas.
 *
 * How it is written says what it is, never how big it is:
 *  - thousands separators and no decimals is dollars written out:
 *    "$100,000" is 0.1, "$999,999" is 0.999999, "100,000,000" is 100;
 *  - seven or more whole digits is dollars too: "1000000" is 1;
 *  - anything else is $mm as typed: "100" is 100, "2.5" is 2.5.
 *
 * A number with separators AND a decimal is $mm: that is how the financial
 * summary prints a figure of a billion or more ("1,204.5"), and a person
 * changing a digit of it is not writing dollars and cents.
 */
export function plainMoney(digits: string, grouped: boolean): number {
	const number = Number(digits);
	const [whole] = digits.split(".");
	const dollars = (grouped && !digits.includes(".")) || whole.replace(/^0+/, "").length >= 7;
	return dollars ? number / 1_000_000 : number;
}

/** A stored value as the person would type it back. */
export function typedFigure(value: number, unit: string): string {
	const shown = unit === "pct" ? value * 100 : value;
	return String(Number(shown.toFixed(6)));
}

// ------------------------------------------------------------------ what changed, what is saved

export interface FigureChange {
	key: string;
	period: string | null;
	label: string;
	before: string;
	after: string;
	note: string;
}

/** Every value typed, as the changes list reads it: "Revenue FY2025A 4.1 → 4.5". */
export function figureChanges(grid: FiguresGrid, draft: FiguresDraft): FigureChange[] {
	const rows = [...grid.series, ...grid.point];
	return draft.entries.map((entry) => {
		const row = rows.find((r) => r.key === entry.key);
		const cell = row?.cells.find((c) => samePeriod(c.period, entry.period));
		const fact = cell?.fact;
		const unit = row?.unit ?? "usd_m";
		return {
			key: entry.key,
			period: entry.period,
			label: `${row?.label ?? entry.key}${entry.period ? ` ${savedColumnName(draft, entry.period) ?? "new column"}` : ""}`,
			before: fact && fact.value !== null ? formatTableValue(fact.value, unit) : "—",
			after: formatTableValue(entry.value, unit),
			// Where it came from, as the changes list reads it: the person's
			// note, else the document (DOP-652).
			note: sourceText(entry.note, entry.source),
		};
	});
}

/**
 * The typed figures as the save dialog lists changes: a value over one the
 * run found is a change, a value where there was none is an addition, and
 * one with no note is flagged as unsourced like a typed number in prose.
 */
export const figureMemoChanges = (changes: FigureChange[], sectionTitle: string): MemoChange[] =>
	changes.map((change) => ({
		sectionKey: "financial_summary",
		sectionTitle,
		kind: change.before === "—" ? "added" : "edited",
		anchor: null,
		summary: `${change.label}: ${change.before} → ${change.after}`,
		text: change.note ? `${change.after} (Source: ${change.note})` : change.after,
		newNumbers: change.note ? [] : [change.after],
	}));

// ------------------------------------------------------------------ the memo's cover (DOP-641)

const missing = (fact: DealScreeningFact): boolean =>
	fact.value === null || fact.state === "gap" || fact.state === "not_available";

/** A company fact as it reads in its field before anyone types: "" when not found. */
export function headerText(fact: DealScreeningFact): string {
	if (missing(fact) || fact.value === null) return "";
	if (fact.unit !== "text" && typeof fact.value === "number") return typedFigure(fact.value, fact.unit);
	return String(fact.value);
}

/** A typed company fact as the server takes it, or null while it is not one yet. */
export function headerValue(edit: HeaderEdit): string | number | null {
	const text = edit.text.trim();
	if (!text) return null;
	return edit.unit === "text" ? text : parseFigure(text, edit.unit);
}

/**
 * The draft with a company fact as typed, and where it came from. Typed
 * back to what the memo says with no source, the edit is taken out; a
 * source on the memo's own value is kept, as a table cell's is. A field
 * cleared stays in the draft, so it can be typed over, but is never saved
 * empty.
 */
export function withHeader(
	draft: FiguresDraft,
	fact: DealScreeningFact,
	text: string,
	note: string,
	/** The document the value came from (DOP-652); kept only while the value is the one it was given for. */
	source?: DocumentSource | null,
): FiguresDraft {
	const was = draft.header.find((e) => e.key === fact.key);
	const document = source ?? (was?.source && was.text.trim() === text.trim() ? was.source : undefined);
	if (text.trim() === headerText(fact).trim() && !note.trim() && !document) return withoutHeader(draft, fact.key);
	const edit: HeaderEdit = { key: fact.key, unit: fact.unit, text, note, ...(document ? { source: document } : {}) };
	const had = draft.header.some((e) => e.key === fact.key);
	return {
		...draft,
		// In place, so the changes list does not reorder as the person types.
		header: had ? draft.header.map((e) => (e.key === fact.key ? edit : e)) : [...draft.header, edit],
	};
}

/**
 * A number typed in a company fact, as its field reads once it is left
 * (DOP-660): "$100,000,000" becomes "100", beside the field's "$mm". Text,
 * and what is not a number yet, stay as typed.
 */
export function withHeaderRead(draft: FiguresDraft, fact: DealScreeningFact): FiguresDraft {
	const edit = draft.header.find((e) => e.key === fact.key);
	if (!edit || edit.unit === "text") return draft;
	const value = headerValue(edit);
	if (typeof value !== "number") return draft;
	const read = typedFigure(value, edit.unit);
	return read === edit.text ? draft : withHeader(draft, fact, read, edit.note, edit.source);
}

/** The field's text as it stands in the draft, or as the memo has it. */
export const headerEditText = (draft: FiguresDraft, fact: DealScreeningFact): string =>
	draft.header.find((edit) => edit.key === fact.key)?.text ?? headerText(fact);

/** The draft with where a company fact came from, its text as it stands. */
export const withHeaderNote = (draft: FiguresDraft, fact: DealScreeningFact, note: string): FiguresDraft =>
	withHeader(draft, fact, headerEditText(draft, fact), note);

export const withoutHeader = (draft: FiguresDraft, key: string): FiguresDraft => ({
	...draft,
	header: draft.header.filter((edit) => edit.key !== key),
});

/** The header edits that change a value, the only ones saved. */
const savedHeader = (draft: FiguresDraft): HeaderEdit[] =>
	draft.header.filter((edit) => headerValue(edit) !== null);

/** Every company fact typed, as the changes list reads it. */
export function headerChanges(facts: DealScreeningFact[], draft: FiguresDraft): (FigureChange & { valid: boolean })[] {
	return draft.header.map((edit) => {
		const fact = facts.find((f) => f.kind === "header" && f.key === edit.key);
		const before = fact ? headerText(fact) : "";
		return {
			key: edit.key,
			period: null,
			label: fact?.label ?? edit.key,
			before: before || "Not found",
			after: edit.text.trim() || "—",
			note: sourceText(edit.note, edit.source),
			valid: headerValue(edit) !== null,
		};
	});
}

/** The typed company facts as the save dialog lists changes. */
export const headerMemoChanges = (changes: FigureChange[], title = "Company facts"): MemoChange[] =>
	changes.map((change) => ({
		sectionKey: "company_facts",
		sectionTitle: title,
		kind: change.before === "Not found" ? "added" : "edited",
		anchor: null,
		summary:
			change.before === change.after
				? `${change.label}: source added`
				: `${change.label}: ${change.before} → ${change.after}`,
		text: change.note ? `${change.after} (Source: ${change.note})` : change.after,
		newNumbers: [],
	}));

// ------------------------------------------------------------------ what is saved

/** Whether the columns or rows were dragged into another order (DOP-649). */
export const isReordered = (draft: FiguresDraft): boolean => Boolean(draft.moved);

/** Whether there is anything to save: a column or row with nothing typed in it keeps nothing. */
export const hasFigureEdits = (draft: FiguresDraft): boolean =>
	draft.entries.length > 0 ||
	savedHeader(draft).length > 0 ||
	Boolean(draft.coverTitle?.trim()) ||
	dealRename(draft) !== null ||
	isReordered(draft);

/** What "Save as a new version" sends for the financial summary and the company facts. */
export function figuresPayload(draft: FiguresDraft): {
	custom_figures?: DealScreeningCustomFigure[];
	facts?: (FigureEntry | DealScreeningHeaderEntry)[];
	company_facts_title?: string;
	financial_order?: { columns?: string[]; rows?: string[] };
} {
	if (!hasFigureEdits(draft)) return {};
	const renamed = rowRenames(draft);
	// A new column saved keeps its place: its order goes with it (codex r3).
	const addsColumn = draft.entries.some((entry) => isNewColumn(entry.period));
	const order = savedOrder(draft, renamed, addsColumn);
	draft = namedColumns(namedRows(draft));
	const title = draft.coverTitle?.replace(/\s+/g, " ").trim();
	if (draft.entries.length === 0 && savedHeader(draft).length === 0) {
		return { ...(title ? { company_facts_title: title } : {}), ...(order ? { financial_order: order } : {}) };
	}
	const used = new Set(draft.entries.map((entry) => entry.key));
	const custom = draft.custom.filter((figure) => used.has(figure.key));
	const header: DealScreeningHeaderEntry[] = savedHeader(draft).map((edit) => ({
		key: edit.key,
		period: null,
		value: headerValue(edit) as string | number,
		note: edit.note.trim(),
		...(edit.source ? { source: sentSource(edit.source) } : {}),
	}));
	return {
		...(custom.length > 0 ? { custom_figures: custom } : {}),
		facts: [
			...draft.entries.map((entry) => ({
				...entry,
				note: entry.note.trim(),
				// The place is sent; the file's name is the server's to read.
				...(entry.source ? { source: sentSource(entry.source) } : {}),
			})),
			...header,
		],
		...(title ? { company_facts_title: title } : {}),
		...(order ? { financial_order: order } : {}),
	};
}

/** The new rows' keys as the server makes them from their names. */
const rowRenames = (draft: FiguresDraft): Map<string, string> =>
	new Map(draft.custom.filter((f) => isNewRow(f.key) && f.label.trim()).map((f) => [f.key, customKey(f.label)]));

/**
 * New columns saved under their names (DOP-649), and a column left unnamed
 * dropped with what was typed under it: a column needs a name.
 */
function namedColumns(draft: FiguresDraft): FiguresDraft {
	return {
		...draft,
		entries: draft.entries.flatMap((entry) => {
			if (!isNewColumn(entry.period)) return [entry];
			const name = savedColumnName(draft, entry.period as string);
			return name ? [{ ...entry, period: name }] : [];
		}),
	};
}

/**
 * The order as saved: columns by name, rows by their saved keys. Sent when
 * the person dragged, or when a new column is saved so it keeps its place;
 * null otherwise. Columns that keep nothing (unnamed, or with nothing under
 * them) are left out.
 */
function savedOrder(
	draft: FiguresDraft,
	renamed: Map<string, string>,
	addsColumn: boolean,
): { columns?: string[]; rows?: string[] } | null {
	if (!isReordered(draft) && !addsColumn) return null;
	const kept = (period: string) =>
		!isNewColumn(period) || draft.entries.some((entry) => entry.period === period);
	const unnamedRows = new Set(draft.custom.filter((f) => !f.label.trim()).map((f) => f.key));
	return {
		...(draft.columnOrder
			? {
					columns: draft.columnOrder
						.filter(kept)
						.map((period) => savedColumnName(draft, period))
						.filter((name): name is string => Boolean(name)),
				}
			: {}),
		...(draft.rowOrder && isReordered(draft)
			? { rows: draft.rowOrder.filter((key) => !unnamedRows.has(key)).map((key) => renamed.get(key) ?? key) }
			: {}),
	};
}

/**
 * New rows keyed by their names as the server keys them, and a row left
 * unnamed dropped with what was typed in it: a figure needs a name.
 */
function namedRows(draft: FiguresDraft): FiguresDraft {
	const unnamed = new Set(draft.custom.filter((f) => !f.label.trim()).map((f) => f.key));
	const rename = rowRenames(draft);
	return {
		...draft,
		custom: draft.custom
			.filter((f) => !unnamed.has(f.key))
			.map((f) => ({ ...f, label: f.label.replace(/\s+/g, " ").trim(), key: rename.get(f.key) ?? f.key })),
		entries: draft.entries
			.filter((e) => !unnamed.has(e.key))
			.map((e) => ({ ...e, key: rename.get(e.key) ?? e.key })),
	};
}

/** A draft read back from this computer, or an empty one. */
export function figuresFromStored(stored: unknown): FiguresDraft {
	if (!stored || typeof stored !== "object") return EMPTY_FIGURES;
	const data = stored as Partial<FiguresDraft>;
	return {
		entries: Array.isArray(data.entries) ? data.entries : [],
		custom: Array.isArray(data.custom) ? data.custom : [],
		periods: Array.isArray(data.periods) ? data.periods : [],
		rows: Array.isArray(data.rows) ? data.rows : [],
		header: Array.isArray(data.header) ? data.header : [],
		coverTitle: typeof data.coverTitle === "string" ? data.coverTitle : null,
		dealName: typeof data.dealName === "string" ? data.dealName : null,
		columnNames: data.columnNames && typeof data.columnNames === "object" ? data.columnNames : {},
		columnOrder: Array.isArray(data.columnOrder) ? data.columnOrder : null,
		rowOrder: Array.isArray(data.rowOrder) ? data.rowOrder : null,
		moved: data.moved === true,
	};
}
